import {
  Box,
  Button,
  Heading,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createPrismaQuery,
  createPrismaSort,
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
  Loosen,
  useCrud,
} from '@pelicargo/datatable';
import { getAsset } from '@pelicargo/tenants';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { accountTypeOptions, prettyDate } from '@pelicargo/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { AccountFormDrawer } from '../components/Drawers/AccountFormDrawer';
import { useAccounts } from '../hooks/accounts/useAccounts';
import { useUnverifyAccount } from '../hooks/accounts/useUnverifyAccounts';
import { useVerifyAccount } from '../hooks/accounts/useVerifyAccount';

const accountFilters = [
  {
    name: 'is_verified',
    label: 'Is Verified',
    type: 'boolean',
    inputType: 'radio',
    operator: 'equals',
    value: undefined,
    options: [
      { label: 'Verified', value: true },
      { label: 'Not Verified', value: false },
    ],
  },
  {
    name: 'type',
    label: 'Account Type',
    type: 'string',
    inputType: 'radio',
    operator: 'equals',
    value: undefined,
    options: accountTypeOptions,
  },
] satisfies DataTableFilter[];

type TableColumns = {
  id: number;
  name: string;
  iata_number: string;
  // Revert to Address when rules 2 gets in
  address: any;
  verified_at: Date;
  parent: { name: string };
  action?: any;
  highlight_until: Date;
};

export const AccountsScreen = () => {
  const accountDrawer = useDisclosure();
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud<typeof accountFilters>();
  const [selectedAccount, setSelectedAccount] = useState<TableColumns | null>(null);

  const {
    data,
    count = 0,
    refetch,
  } = useAccounts({
    skip,
    take,
    search: query,
    orderBy: createPrismaSort(sorting),
    filters: createPrismaQuery(filters),
  });
  const [verifyAccount] = useVerifyAccount();
  const [unverifyAccount] = useUnverifyAccount();

  const toggleVerification = useCallback(
    (id: number, isVerified: boolean) => async () => {
      try {
        const successMessage = isVerified ? 'Account unverified.' : 'Account verified.';
        if (isVerified) await unverifyAccount({ id });
        else await verifyAccount({ id });

        refetch();
        handleSuccess(successMessage);
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, handleSuccess, refetch, unverifyAccount, verifyAccount],
  );

  const handleAddFilter = (filter: Loosen<typeof accountFilters>[0]) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClear = () => {
    setFilters([]);
  };

  const onRowClick = useCallback(
    (row: TableColumns) => {
      setSelectedAccount(row);
      accountDrawer.onOpen();
    },
    [accountDrawer],
  );

  const onCloseCallback = () => {
    setSelectedAccount(null);
    refetch();
    accountDrawer.onClose();
  };

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        minSize: 0,
        size: 0,
        header: 'ID',
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        size: Number.MIN_SAFE_INTEGER,
        cell: (cell) => {
          const isHighlighted =
            cell.row.original.highlight_until && new Date() < new Date(cell.row.original.highlight_until);
          return (
            <Stack direction="row" spacing="1">
              <Show if={isHighlighted}>
                <Image src={getAsset('sparkle.svg')} />
              </Show>
              <Text>{cell.getValue()}</Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor('iata_number', {
        header: 'IATA',
        minSize: 0,
        size: 0,
      }),
      columnHelper.accessor('address', {
        minSize: 0,
        size: 0,
        header: 'Address',
        cell: (cell) => {
          const address = cell.getValue();
          return `${address?.city}, ${address?.country}`;
        },
      }),
      columnHelper.accessor('verified_at', {
        minSize: 0,
        size: 0,
        header: 'Verified At',
        cell: (cell) => prettyDate(cell.getValue()),
      }),
      columnHelper.accessor('parent.name', {
        header: 'Parent',
        minSize: 0,
        size: 0,
        cell: (cell) => cell.getValue() ?? '-',
      }),
      columnHelper.accessor('action', {
        header: '',
        minSize: 0,
        size: 0,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => onRowClick(row)}>Update</MenuItem>
                  <MenuItem onClick={toggleVerification(row?.id, !!row?.verified_at)}>
                    {row?.verified_at ? 'Unverify' : 'Verify'}
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, toggleVerification, onRowClick],
  );

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" justify="space-between" zIndex="99" w="full">
          <Stack direction="row" align="center" w="full">
            <Heading variant="h3">Accounts</Heading>
            <DataTableSearch value={query} onChange={handleQueryChange} />
            <DataTableAddFilter
              activeFilters={filters?.map((filter) => filter.name)}
              options={accountFilters}
              handleAddFilter={handleAddFilter}
            />
          </Stack>
          <Button colorScheme="brand" onClick={accountDrawer.onOpen}>
            Create Account
          </Button>
        </Stack>
        <Show if={filters.length > 0}>
          <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
        </Show>
      </Stack>
      <DataTable
        columns={columns}
        data={data ?? []}
        dataCount={count ?? 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={onRowClick}
      />
      <AccountFormDrawer isOpen={accountDrawer.isOpen} onClose={onCloseCallback} accountId={selectedAccount?.id} />
    </Stack>
  );
};
